/**
 * FileContextDropZone Component
 *
 * Full-page drop zone overlay that appears when files are dragged over.
 * Used for drag-and-drop file context in AI chat pages.
 */

import { useState, useEffect, useRef } from 'react';
import { Upload } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface FileContextDropZoneProps {
  /** Callback when files are dropped */
  onFilesDropped: (files: FileList) => void;
  /** Whether the drop zone is disabled */
  disabled?: boolean;
  /** Children to wrap */
  children: React.ReactNode;
  /** Additional CSS classes for the container */
  className?: string;
}

/**
 * Drop zone wrapper that shows overlay when files are dragged over
 */
export function FileContextDropZone({
  onFilesDropped,
  disabled = false,
  children,
  className,
}: FileContextDropZoneProps) {
  const [isDragActive, setIsDragActive] = useState(false);
  const dragCounter = useRef(0);

  // Handle drag events at window level for full-page drop zone
  useEffect(() => {
    if (disabled) return;

    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Only activate for file drags
      if (e.dataTransfer?.types.includes('Files')) {
        dragCounter.current++;
        if (dragCounter.current === 1) {
          setIsDragActive(true);
        }
      }
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      dragCounter.current--;
      if (dragCounter.current === 0) {
        setIsDragActive(false);
      }
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      dragCounter.current = 0;
      setIsDragActive(false);

      if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        onFilesDropped(e.dataTransfer.files);
      }
    };

    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('drop', handleDrop);

    return () => {
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('drop', handleDrop);
    };
  }, [disabled, onFilesDropped]);

  // Reset drag counter when disabled changes
  useEffect(() => {
    if (disabled) {
      dragCounter.current = 0;
      setIsDragActive(false);
    }
  }, [disabled]);

  return (
    <div className={cn('relative h-full', className)}>
      {children}

      {/* Drop overlay */}
      {isDragActive && !disabled && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4 p-8 rounded-xl border-2 border-dashed border-primary bg-primary/5">
            <Upload className="h-16 w-16 text-primary" />
            <div className="text-center">
              <p className="text-lg font-semibold text-foreground">
                Drop files to add as context
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Supported: text files, .docx, .pdf
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FileContextDropZone;
